(() => {
  if (customElements.get('birthday-personalization')) return;

  class BirthdayPersonalization extends HTMLElement {
    connectedCallback() {
      if (this.abortController) return;

      this.form = this.closest('form');
      if (!this.form) return;

      this.abortController = new AbortController();
      const signal = this.abortController.signal;

      this.error = this.querySelector('[data-birthday-error]');
      this.status = this.querySelector('[data-birthday-status]');
      this.photo = this.querySelector('[data-birthday-photo]');
      this.phone = this.querySelector('[data-birthday-phone]');

      this.form.enctype = 'multipart/form-data';

      // Validate here so off-screen errors are shown clearly.
      this.form.noValidate = true;

      this.form.addEventListener(
        'submit',
        event => this.submit(event),
        { capture: true, signal }
      );

      // Handle Buy Now before the theme's delegated click actions.
      this.form.addEventListener(
        'click',
        event => {
          const button = event.target.closest?.(
            '[data-birthday-checkout]'
          );

          if (
            !button ||
            button.form !== this.form ||
            button.disabled
          ) return;

          this.submit(event, button);
        },
        { capture: true, signal }
      );

      this.phone.addEventListener(
        'input',
        () => this.validatePhone(),
        { signal }
      );

      this.photo?.addEventListener(
        'change',
        () => this.updatePhoto(),
        { signal }
      );

      this.querySelector('[data-birthday-remove]')
        ?.addEventListener(
          'click',
          () => {
            this.photo.value = '';
            this.updatePhoto();
            this.photo.focus();
          },
          { signal }
        );

      this.observer = new MutationObserver(
        () => this.syncAvailability()
      );

      this.observer.observe(this.form, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['disabled']
      });

      this.syncAvailability();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.abortController = null;
      this.observer?.disconnect();

      if (this.previewURL) {
        URL.revokeObjectURL(this.previewURL);
      }

      this.previewURL = null;
    }

    syncAvailability() {
      if (this.busy || this.added) return;

      const addButton = this.form.querySelector('[name="add"]');
      const buyButton = this.form.querySelector(
        '[data-birthday-checkout]'
      );

      if (
        addButton &&
        buyButton &&
        buyButton.disabled !== addButton.disabled
      ) {
        buyButton.disabled = addButton.disabled;
      }
    }

    validatePhone() {
      const digits = this.phone.value.replace(/\D/g, '');

      const valid =
        /^[+\d\s().-]+$/.test(this.phone.value) &&
        digits.length >= 7 &&
        digits.length <= 15;

      this.phone.setCustomValidity(
        valid
          ? ''
          : 'Enter a valid WhatsApp number, including country code.'
      );
    }

    validatePhoto() {
      if (!this.photo) return true;

      const file = this.photo.files[0];
      let message = '';

      if (file) {
        const allowed = [
          'image/jpeg',
          'image/png',
          'image/webp'
        ];

        if (!allowed.includes(file.type)) {
          message = 'Please choose a JPG, PNG or WebP photo.';
        } else if (!file.size) {
          message = 'This file is empty. Please choose another photo.';
        } else if (file.size > 10 * 1024 * 1024) {
          message = 'Please choose a photo smaller than 10 MB.';
        }
      }

      this.photo.setCustomValidity(message);

      return !message && Boolean(file);
    }

    updatePhoto() {
      if (this.previewURL) {
        URL.revokeObjectURL(this.previewURL);
      }

      this.previewURL = null;

      const preview = this.querySelector(
        '[data-birthday-preview]'
      );

      const image = this.querySelector(
        '[data-birthday-preview-image]'
      );

      preview.hidden = true;
      image.removeAttribute('src');

      if (this.validatePhoto()) {
        this.previewURL = URL.createObjectURL(
          this.photo.files[0]
        );

        image.src = this.previewURL;
        preview.hidden = false;
      } else if (this.photo.files.length) {
        this.photo.reportValidity();
      }
    }

    showError(message, cartLink = false) {
      this.error.textContent = message;

      if (cartLink) {
        const link = document.createElement('a');
        link.href = this.dataset.cartUrl;
        link.textContent = ' Open cart';
        this.error.append(link);
      }

      this.error.hidden = false;
      this.error.focus();
    }

    setBusy(value) {
      this.busy = value;
      this.form.setAttribute('aria-busy', String(value));

      if (value) {
        this.controlStates = Array.from(this.form.elements)
          .filter(el =>
            [
              'INPUT',
              'TEXTAREA',
              'SELECT',
              'BUTTON',
              'FIELDSET'
            ].includes(el.tagName)
          )
          .map(el => [el, el.disabled]);

        this.controlStates.forEach(([el]) => {
          el.disabled = true;
        });
      } else {
        this.controlStates?.forEach(([el, disabled]) => {
          if (el.isConnected) {
            el.disabled = disabled;
          }
        });

        this.syncAvailability();
      }
    }

    async submit(event, submitter = event.submitter) {
      event.preventDefault();
      event.stopImmediatePropagation();

      if (this.busy || this.added) return;

      this.error.hidden = true;
      this.status.textContent = '';

      this.validatePhone();
      this.validatePhoto();

      this.querySelectorAll(
        'input[type="text"], textarea'
      ).forEach(input => {
        input.setCustomValidity(
          input.required && !input.value.trim()
            ? 'Please fill out this field.'
            : ''
        );

        input.oninput = () => input.setCustomValidity('');
      });

      if (!this.form.checkValidity()) {
        const invalid = this.form.querySelector(
          ':invalid:not(fieldset)'
        );

        const label = invalid?.labels?.[0]?.textContent
          .replace(/\*/g, '')
          .trim();

        this.showError(
          label
            ? `Please check ${label}: ${invalid.validationMessage}`
            : 'Please complete all required details and check your photo.'
        );

        if (invalid) {
          invalid.scrollIntoView({
            block: 'center',
            behavior: 'instant'
          });

          invalid.focus({ preventScroll: true });
          invalid.reportValidity();
        }

        return;
      }

      const addButton = this.form.querySelector('[name="add"]');
      if (addButton?.disabled) return;

      // Capture all details and the actual file before disabling inputs.
      const body = new FormData(this.form);

      if (!body.get('id')) {
        this.showError(
          'Please select an available product option.'
        );
        return;
      }

      const toCheckout = Boolean(
        submitter?.matches('[data-birthday-checkout]')
      );

      this.setBusy(true);

      this.status.textContent = this.photo
        ? 'Uploading your photo and saving details…'
        : 'Saving your details…';

      try {
        const response = await fetch(this.dataset.addUrl, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json'
          },
          body
        });

        const result = await response.json();

        if (!response.ok) {
          this.showError(
            typeof result.description === 'string'
              ? result.description
              : 'This item could not be added. Check availability and try again.'
          );

          this.status.textContent = '';
          this.setBusy(false);
          return;
        }

        // Prevent a successful add from being submitted twice.
        this.added = true;

        const item = result.items?.[0] || result;

        if (
          this.photo &&
          !item.properties?.['Customer photo']
        ) {
          this.status.textContent = '';

          this.showError(
            'The item was added, but its photo was not confirmed. Open your cart and remove this item before trying again.',
            true
          );

          return;
        }

        this.status.textContent = toCheckout
          ? 'Details saved. Opening checkout…'
          : 'Details saved. Opening your cart…';

        window.location.assign(
          toCheckout
            ? this.dataset.checkoutUrl
            : this.dataset.cartUrl
        );
      } catch (error) {
        this.status.textContent = '';
        this.setBusy(false);

        this.showError(
          'We could not confirm the result. Check your cart before trying again to avoid adding the item twice.',
          true
        );
      }
    }
  }

  customElements.define(
    'birthday-personalization',
    BirthdayPersonalization
  );
})();